'use client';

import type { SearchResult } from 'conversational-search-client';
import { type JSX } from 'react';

import { JustifiedGallery } from '@videoblocks/design-system';

import { useChatSessionContext } from '../../../_context/SessionContext';
import { useTopResults } from '../../../_hooks';
import { AllResultsSection } from '../AllResultsSection';
import { TopResultsSection } from '../TopResultsSection';
import { isVideoBucketResult } from '../resultBuckets';
import { VideoResultCard } from './VideoResultCard';
import { deriveAspectRatio } from './deriveAspectRatio';

interface VideoResultsGridProps {
    results: SearchResult[];
}

// The tray shows at most this many cited results in a 4-column grid (two rows max).
const MAX_TOP_RESULTS = 8;

function toGalleryItems(results: SearchResult[]): Array<SearchResult & { id: number; aspectRatio: number }> {
    return results.map((result) => ({
        ...result,
        id: result.contentId,
        aspectRatio: deriveAspectRatio(result.orientation),
    }));
}

export function VideoResultsGrid({ results }: VideoResultsGridProps): JSX.Element {
    const { showTopResults, supportsImages } = useChatSessionContext();
    const topResults = useTopResults()
        .filter((result) => isVideoBucketResult(result, supportsImages))
        .slice(0, MAX_TOP_RESULTS);
    const hasTopResults = topResults.length > 0;
    // The tray is additive: cited items also remain in the full results grid below.
    const items = toGalleryItems(results);

    if (!showTopResults) {
        return (
            <div className="flex flex-col gap-3 p-4">
                <JustifiedGallery items={items} rowGapPx={8} columnGapPx={8}>
                    {({ item }) => <VideoResultCard result={item} />}
                </JustifiedGallery>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {hasTopResults && (
                <TopResultsSection count={topResults.length}>
                    <JustifiedGallery items={toGalleryItems(topResults)} rowGapPx={8} columnGapPx={8}>
                        {({ item }) => <VideoResultCard result={item} isTopResult />}
                    </JustifiedGallery>
                </TopResultsSection>
            )}
            <AllResultsSection count={results.length}>
                <JustifiedGallery items={items} rowGapPx={8} columnGapPx={8}>
                    {({ item }) => <VideoResultCard result={item} />}
                </JustifiedGallery>
            </AllResultsSection>
        </div>
    );
}
